import type { Map as MapLibreMap } from "maplibre-gl";
import type { FeatureCollection, Point } from "geojson";
import { createRegionLabelLayer, createFixedLabelLayer } from "@wondermaps/maps";
import type { MapLabel } from "../MapCanvas";

/**
 * Load a sponsor logo image into MapLibre via canvas.
 * Fits logo into a square canvas (maxSize × maxSize) maintaining aspect ratio.
 * Returns the sprite ID if successful.
 */
async function loadSponsorLogo(
  map: MapLibreMap,
  labelId: string,
  logoUrl: string,
  maxSize = 64
): Promise<string | null> {
  const spriteId = `label-sponsor-${labelId}`;
  if (map.hasImage(spriteId)) return spriteId;

  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load logo: ${logoUrl}`));
      img.src = logoUrl;
    });

    const dpr = window.devicePixelRatio;
    const canvasSize = maxSize * dpr;
    const outerPad = 4 * dpr;
    const innerPad = 6 * dpr;
    const cornerRadius = 6 * dpr;

    const canvas = document.createElement("canvas");
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // White rounded-rect background with subtle border
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
    ctx.lineWidth = dpr;
    ctx.beginPath();
    ctx.roundRect(outerPad, outerPad, canvasSize - outerPad * 2, canvasSize - outerPad * 2, cornerRadius);
    ctx.fill();
    ctx.stroke();

    // Fit logo inside inner padding area
    const logoArea = canvasSize - (outerPad + innerPad) * 2;
    const scale = Math.min(logoArea / img.width, logoArea / img.height);
    const drawW = img.width * scale;
    const drawH = img.height * scale;

    // Center logo in canvas
    ctx.drawImage(
      img,
      (canvasSize - drawW) / 2,
      (canvasSize - drawH) / 2,
      drawW,
      drawH
    );

    const imageData = ctx.getImageData(0, 0, canvasSize, canvasSize);
    map.addImage(spriteId, imageData, { pixelRatio: dpr });
    return spriteId;
  } catch {
    return null;
  }
}

/**
 * Add map labels source and layers.
 * Region labels render below fixed labels, both below POIs.
 * Sponsor logos are loaded lazily — labels render as text immediately,
 * logos appear once images are fetched and added to MapLibre.
 */
export function addLabelsToMap(map: MapLibreMap, labels: MapLabel[], zoomOffset?: number): void {
  if (labels.length === 0) return;

  // Collect labels with sponsor logos for lazy loading
  const sponsoredLabels: Array<{ labelId: string; logoUrl: string }> = [];

  const labelFeatures: FeatureCollection<Point> = {
    type: "FeatureCollection",
    features: labels
      .filter((label) => label.location !== null)
      .map((label) => {
        // Track sponsored labels for logo loading
        if (label.sponsorId && label.sponsorLogoUrl) {
          sponsoredLabels.push({
            labelId: label.id,
            logoUrl: label.sponsorLogoUrl,
          });
        }

        return {
          type: "Feature" as const,
          properties: {
            id: label.id,
            text: label.text,
            type: label.type,
            fontSize: label.fontSize,
            fontWeight: label.fontWeight,
            color: label.color,
            rotation: label.rotation,
            minZoom: label.minZoom,
            maxZoom: label.maxZoom,
            opacity: label.opacity,
            backgroundColor: label.backgroundColor,
            haloWidth: label.haloWidth,
            // Sponsor image ID set after logo loads — empty string means no icon
            ...(label.sponsorId && label.sponsorLogoUrl
              ? { sponsorImageId: `label-sponsor-${label.id}` }
              : {}),
          },
          geometry: label.location!,
        };
      }),
  };

  map.addSource("map-labels", {
    type: "geojson",
    data: labelFeatures,
  });

  // Fixed labels first (below region labels)
  map.addLayer(createFixedLabelLayer("map-labels-fixed", "map-labels", zoomOffset));
  // Region labels second (above fixed, below POIs)
  map.addLayer(createRegionLabelLayer("map-labels-region", "map-labels"));

  // Lazy-load sponsor logos post-init
  if (sponsoredLabels.length > 0) {
    const loadLogos = async () => {
      for (const sl of sponsoredLabels) {
        await loadSponsorLogo(map, sl.labelId, sl.logoUrl);
      }
      // MapLibre auto-repaints when addImage completes — logos appear once loaded
    };

    if ("requestIdleCallback" in window) {
      (window as Window).requestIdleCallback(() => void loadLogos(), { timeout: 1000 });
    } else {
      setTimeout(() => void loadLogos(), 200);
    }
  }
}
